/**
 * Vector objects and path geometry (Pen, Shaper, Knife, Shape tool grids,
 * object transforms, the Vector inspector's structural operations, object
 * groups, node and object deletion) as editor commands.
 *
 * Appearance and effects (recolour, blends, offset paths, Live Paint,
 * Transform Each, opacity, appearance stacks, Graphic Styles) are the
 * vector appearance commands (vector-appearance.ts), not these.
 *
 * Every command takes serializable, ID-addressed params and compiles to the
 * native commands the UI executes (`UpdateVectorSceneCommand`,
 * `UpdateVectorObjectCommand`, `DeletePathNodesCommand`,
 * `RemoveVectorObjectCommand`, `TagObjectGroupCommand`, the multi-object
 * transform commands, `AddLayerCommand`). Closures are built inside `compile`
 * from the params; they never cross the protocol boundary.
 *
 * Bundle: this module is reachable from user-dispatch.ts (through the command
 * registry), so it must not import the vector runtime. The runtime classes and
 * vector algorithms come from `vector-kit.ts`, loaded lazily: agents load it in
 * `prepare`; the UI loads it before `runCommand` (the vector inspector imports
 * it statically, other call sites `await loadVectorKit()`). A synchronous run
 * before the kit is loaded is rejected, never half-applied.
 *
 * Coordinates: points that come from the canvas (Pen, Shaper, Knife, shape
 * rectangles, transform origins) are in document pixels, as the tools see
 * them. Path node coordinates in `vector.setPathNodes` are in the path
 * object's own geometry space (before the object and layer transforms).
 */
import type { LayerId, PaintInput, Placement, Ref, StrokeInput } from '@maipo/agent-protocol';
import { AddLayerCommand, noOp, type Command, type CommandExecution, type MatrixTransformCommand } from '../../core/commands';
import { damage } from '../../core/damage';
import { IDENTITY_MATRIX, invertMatrix, multiplyMatrix, transformPoint, transformRectBoundsPrecise, type Matrix, type Point, type Rect } from '../../core/geometry';
import { createId, findLayerParent, layerDocumentTransform, vectorObjectLocalBounds, type EditorDocument, type VectorLayer } from '../../core/model';
import { activePerspectivePlane } from '../../core/perspective';
import { transformForPlacement, vectorDrawPlacement, type VectorDrawMode, type VectorDrawPlacement } from '../../core/vector-draw-mode';
import { hasGroupedObject, TagObjectGroupCommand } from '../../vector/object-groups';
import type { NodeContinuity, PathGeometry, PathNode, PathObject, VectorObject } from '../../vector/types';
import { IntentRejection, type CompiledStep, type Diagnostic, type Footprint, type ValidateContext } from '../registry';
import { checkContainerUnlocked, checkUnlocked, error, layerFor, placementReads, requireLayer, resolvePlacement, toSolidPaint, toStroke, validatePlacement, type LockMode } from '../intents/common';
import { same, singleLayerFootprint } from '../intents/properties';
import { defineCommand, type EditorCommand } from './registry';
import type { VectorKit } from './vector-kit';

// ---------------------------------------------------------------------------
// The lazily loaded vector kit
// ---------------------------------------------------------------------------

let kit: VectorKit | null = null;

/** Called by vector-kit.ts when it is evaluated. */
export function provideVectorKit(value: VectorKit): void {
  kit = value;
}

/** Loads the vector runtime and algorithms the vector object commands compile to. */
export async function loadVectorKit(): Promise<VectorKit> {
  if (!kit) await import('./vector-kit');
  if (!kit) throw new Error('The vector kit did not register itself.');
  return kit;
}

/** The kit when it is loaded, or null. */
export function loadedVectorKit(): VectorKit | null {
  return kit;
}

function kitFor(prepared: unknown): VectorKit {
  const value = (prepared as VectorKit | undefined) ?? kit;
  if (!value) throw new IntentRejection('COMMAND_REJECTED', 'The vector runtime is not loaded yet; run this command through the dispatcher or after loadVectorKit().');
  return value;
}

const lazyKit = { prepare: () => loadVectorKit() };

// ---------------------------------------------------------------------------
// Shared params, lookups and checks
// ---------------------------------------------------------------------------

type ObjectId = string;
interface LayerParams { layer: Ref }
interface ObjectsParams extends LayerParams { objects: ObjectId[] }
interface ObjectParams extends LayerParams { object: ObjectId }

const json = (value: unknown) => JSON.stringify(value).slice(0, 80);

function vectorLayerFor(document: EditorDocument, ref: Ref, context: ValidateContext, diagnostics: Diagnostic[], mode: LockMode = 'any'): VectorLayer | null {
  const layer = layerFor(document, ref, context, diagnostics);
  if (!layer) return null;
  if (layer.kind !== 'vector') { diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not vector.`, layer.id)); return null; }
  checkUnlocked(document, layer, mode, diagnostics, context.policy);
  return layer;
}

function requireVectorLayer(document: EditorDocument, ref: Ref, aliases: ReadonlyMap<string, LayerId>): VectorLayer {
  const layer = requireLayer(document, ref, aliases);
  if (layer.kind !== 'vector') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not vector.`, layer.id);
  return layer;
}

function objectOn(layer: VectorLayer, id: ObjectId): VectorObject | undefined {
  return layer.vector.objects.find((object) => object.id === id);
}

function checkObjects(layer: VectorLayer, ids: readonly ObjectId[], diagnostics: Diagnostic[], minimum = 1): VectorObject[] {
  if (new Set(ids).size !== ids.length) diagnostics.push(error('COMMAND_REJECTED', 'Object ids must be unique.', layer.id));
  const found: VectorObject[] = [];
  for (const id of ids) {
    const object = objectOn(layer, id);
    if (object) found.push(object);
    else diagnostics.push(error('LAYER_NOT_FOUND', `Vector object ${json(id)} does not exist on ${layer.id}.`, layer.id));
  }
  if (ids.length < minimum) diagnostics.push(error('COMMAND_REJECTED', `This operation needs at least ${minimum} object${minimum === 1 ? '' : 's'}.`, layer.id));
  return found;
}

function checkPath(layer: VectorLayer, id: ObjectId, diagnostics: Diagnostic[]): PathObject | null {
  const object = objectOn(layer, id);
  if (!object) { diagnostics.push(error('LAYER_NOT_FOUND', `Vector object ${json(id)} does not exist on ${layer.id}.`, layer.id)); return null; }
  if (object.type !== 'path') { diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Object ${object.id} is a ${object.type}, not a path.`, layer.id)); return null; }
  return object;
}

function requirePath(layer: VectorLayer, id: ObjectId): PathObject {
  const object = objectOn(layer, id);
  if (object?.type !== 'path') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Object ${json(id)} is not a path on ${layer.id}.`, layer.id);
  return object;
}

function checkNodes(objects: readonly VectorObject[], nodeIds: readonly string[], diagnostics: Diagnostic[], layer: LayerId): void {
  const known = new Set(objects.flatMap((object) => object.type === 'path' ? object.geometry.nodes.map((node) => node.id) : []));
  for (const id of nodeIds) if (!known.has(id)) diagnostics.push(error('LAYER_NOT_FOUND', `Path node ${json(id)} does not exist on the given objects.`, layer));
}

/** Validation of an operation on one vector layer and a list of its objects. */
function validateObjects(document: EditorDocument, params: ObjectsParams, context: ValidateContext, minimum = 1, mode: LockMode = 'any'): { diagnostics: Diagnostic[]; layer: VectorLayer | null; objects: VectorObject[] } {
  const diagnostics: Diagnostic[] = [];
  const layer = vectorLayerFor(document, params.layer, context, diagnostics, mode);
  const objects = layer ? checkObjects(layer, params.objects, diagnostics, minimum) : [];
  return { diagnostics, layer, objects };
}

const layerFootprint = (_document: EditorDocument, params: LayerParams, aliases: ReadonlyMap<string, LayerId>): Footprint => singleLayerFootprint(params.layer, aliases);

// ---------------------------------------------------------------------------
// Creating vector layers (Pen, Shape tool, grids, Shaper)
// ---------------------------------------------------------------------------

interface PointInput { x: number; y: number }
interface NodeInput { id?: string; x: number; y: number; in?: PointInput | null; out?: PointInput | null; type?: NodeContinuity; cornerRadius?: number }
interface SubpathInput { id?: string; closed?: boolean; nodes: NodeInput[] }

interface CreateParams {
  /** Explicit placement; otherwise `drawMode` (default normal: top of the document). */
  to?: Placement;
  /** The Shape/Pen tools' Draw Normal / Behind / Inside, relative to the active layer. */
  drawMode?: VectorDrawMode;
  as?: string;
}

function point(input: PointInput): Point {
  return { x: input.x, y: input.y };
}

/** A node as the Pen tool builds it: anchor, handles, continuity (handles make it smooth by default). */
function nodeShape(input: NodeInput): Omit<PathNode, 'id' | 'subpathId'> {
  const incoming = input.in ? point(input.in) : null;
  const outgoing = input.out ? point(input.out) : null;
  return {
    anchor: point(input),
    incoming,
    outgoing,
    continuity: input.type ?? (incoming || outgoing ? 'smooth' : 'corner'),
    ...(input.cornerRadius ? { cornerRadius: input.cornerRadius } : {}),
  };
}

function createPlacement(document: EditorDocument, params: CreateParams, aliases: ReadonlyMap<string, LayerId>): VectorDrawPlacement {
  if (!params.to) return vectorDrawPlacement(document, params.drawMode ?? 'normal');
  const target = resolvePlacement(document, params.to, aliases);
  const parentTransform = target.parent === document.root ? IDENTITY_MATRIX : layerDocumentTransform(document.root, target.parent.id) ?? IDENTITY_MATRIX;
  return { parentId: target.parentId, index: target.index, clipping: false, parentTransform, effectiveMode: 'normal' };
}

function createFootprint(document: EditorDocument, params: CreateParams, aliases: ReadonlyMap<string, LayerId>): Footprint {
  const reads = placementReads(document, params.to, aliases);
  if (!params.to && params.drawMode && params.drawMode !== 'normal') {
    const active = document.activeLayerIds[0];
    const location = active ? findLayerParent(document.root, active) : undefined;
    if (active) reads.push(active);
    if (location) reads.push(location.parent === document.root ? 'root' : location.parent.id);
  }
  return { reads: [...new Set(reads)], writes: [], structural: true, document: false };
}

function validateCreate(document: EditorDocument, params: CreateParams, context: ValidateContext, diagnostics: Diagnostic[]): void {
  if (params.to) { validatePlacement(document, params.to, context, diagnostics); return; }
  if (!params.drawMode || params.drawMode === 'normal') return;
  const active = document.activeLayerIds[0];
  const location = active ? findLayerParent(document.root, active) : undefined;
  if (location && active) checkContainerUnlocked(document, location.parent, diagnostics, active, context.policy);
}

function place(document: EditorDocument, layer: VectorLayer, params: CreateParams, aliases: Map<string, LayerId>, documentTransform: Matrix): CompiledStep[] {
  const placement = createPlacement(document, params, aliases);
  layer.clipping = placement.clipping;
  layer.transform = transformForPlacement(documentTransform, placement);
  if (params.as) aliases.set(params.as, layer.id);
  return [new AddLayerCommand(layer, placement.parentId, placement.index)];
}

function applyStyle(layer: VectorLayer, object: VectorObject | undefined, params: { name?: string; fill?: PaintInput | null; stroke?: StrokeInput | null }): void {
  if (!object) return;
  if (params.name !== undefined) { layer.name = params.name; object.name = params.name; }
  if (params.fill !== undefined) object.fill = params.fill ? toSolidPaint(params.fill) : null;
  if (params.stroke !== undefined) object.stroke = params.stroke ? toStroke(params.stroke) : null;
}

type CreatePathParams = CreateParams & { name?: string; subpaths: SubpathInput[]; fillRule?: 'nonzero' | 'evenodd'; fill?: PaintInput | null; stroke?: StrokeInput | null };

export const createPath = defineCommand<CreatePathParams>({
  id: 'vector.createPath',
  group: 'vector',
  createsLayer: true,
  backs: ['tool.pen'],
  label: (params) => `Add ${params.name ?? 'Path'}`,
  footprint: (document, params, aliases) => createFootprint(document, params, aliases),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    if (!params.subpaths.length || params.subpaths.some((subpath) => !subpath.nodes.length)) diagnostics.push(error('COMMAND_REJECTED', 'A path needs at least one subpath with at least one node.'));
    validateCreate(document, params, context, diagnostics);
    return diagnostics;
  },
  ...lazyKit,
  compile: (document, params, prepared, aliases) => {
    const vector = kitFor(prepared);
    const [first, ...rest] = params.subpaths;
    if (!first) throw new IntentRejection('COMMAND_REJECTED', 'A path needs at least one subpath.');
    // Exactly the Pen tool's construction for the first subpath (same ids, same defaults).
    const layer = vector.createPathLayer(first.nodes.map(nodeShape), first.closed ?? false);
    const object = layer.vector.objects[0];
    if (object?.type === 'path') {
      for (const subpath of rest) {
        const subpathId = createId('subpath');
        const nodes: PathNode[] = subpath.nodes.map((node) => ({ ...nodeShape(node), id: createId('node'), subpathId }));
        object.geometry = { ...object.geometry, nodes: [...object.geometry.nodes, ...nodes], subpaths: [...object.geometry.subpaths, { id: subpathId, nodeIds: nodes.map((node) => node.id), closed: subpath.closed ?? false }] };
      }
      if (params.fillRule) object.geometry = { ...object.geometry, fillRule: params.fillRule };
    }
    applyStyle(layer, object, params);
    return place(document, layer, params, aliases, layer.transform);
  },
});

type CreateShapeParams = CreateParams & { shape: 'rectangle' | 'ellipse'; rect: Rect; radius?: number; name?: string; fill?: PaintInput | null; stroke?: StrokeInput | null };

export const createShape = defineCommand<CreateShapeParams>({
  id: 'vector.createShape',
  group: 'vector',
  createsLayer: true,
  backs: ['tool.shape.rectangle', 'tool.shape.ellipse'],
  label: (params) => `Add ${params.name ?? (params.shape === 'rectangle' ? 'Rectangle' : 'Ellipse')}`,
  footprint: (document, params, aliases) => createFootprint(document, params, aliases),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    if (params.shape === 'ellipse' && params.radius !== undefined) diagnostics.push(error('COMMAND_REJECTED', 'Ellipses have no corner radius.'));
    validateCreate(document, params, context, diagnostics);
    return diagnostics;
  },
  ...lazyKit,
  compile: (document, params, prepared, aliases) => {
    const vector = kitFor(prepared);
    const { rect } = params;
    const layer = vector.createShapeLayer(params.shape);
    const steps = place(document, layer, params, aliases, [1, 0, 0, 1, rect.x, rect.y]);
    // The Shape tool's geometry: origin at the layer, radius clamped to half the short side.
    const object = layer.vector.objects[0];
    if (params.shape === 'rectangle' && object?.type === 'rectangle') {
      const maximum = Math.min(rect.width / 2, rect.height / 2);
      const radius = params.radius === undefined ? object.geometry.radius : { topLeft: params.radius, topRight: params.radius, bottomRight: params.radius, bottomLeft: params.radius };
      object.geometry = { ...object.geometry, x: 0, y: 0, width: Math.max(1, rect.width), height: Math.max(1, rect.height), radius: Object.fromEntries(Object.entries(radius).map(([key, value]) => [key, Math.min(value, maximum)])) as typeof object.geometry.radius };
    } else if (params.shape === 'ellipse' && object?.type === 'ellipse') object.geometry = { x: 0, y: 0, width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
    applyStyle(layer, object, params);
    return steps;
  },
});

type CreateGridParams = CreateParams & { kind: 'rectangular' | 'polar'; rect: Rect; horizontalDividers?: number; verticalDividers?: number; concentricDividers?: number; radialDividers?: number };

export const createGrid = defineCommand<CreateGridParams>({
  id: 'vector.createGrid',
  group: 'vector',
  createsLayer: true,
  backs: ['tool.shape.rectangular-grid', 'tool.shape.polar-grid'],
  label: (params) => `Add ${params.kind === 'polar' ? 'Polar Grid' : 'Rectangular Grid'}`,
  footprint: (document, params, aliases) => createFootprint(document, params, aliases),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    validateCreate(document, params, context, diagnostics);
    return diagnostics;
  },
  ...lazyKit,
  compile: (document, params, prepared, aliases) => {
    const vector = kitFor(prepared);
    const { rect } = params;
    // The Shape tool's defaults (Workspace gridSettings).
    const settings = {
      horizontalDividers: params.horizontalDividers ?? 5,
      verticalDividers: params.verticalDividers ?? 5,
      concentricDividers: params.concentricDividers ?? 4,
      radialDividers: params.radialDividers ?? 8,
    };
    const layer = vector.createGridLayer(params.kind === 'polar' ? 'polar-grid' : 'rectangular-grid', Math.max(1, rect.width), Math.max(1, rect.height), settings);
    return place(document, layer, params, aliases, [1, 0, 0, 1, rect.x, rect.y]);
  },
});

type ShaperParams = CreateParams & { points: PointInput[] };

const SHAPER_NAMES = { rectangle: 'Shaper Rectangle', ellipse: 'Shaper Ellipse', triangle: 'Shaper Triangle' } as const;

export const shaper = defineCommand<ShaperParams>({
  id: 'vector.shaper',
  group: 'vector',
  createsLayer: true,
  backs: ['tool.shaper'],
  label: (params) => {
    const recognition = kit?.recognizeShaperGesture(params.points.map(point));
    return recognition ? `Add ${SHAPER_NAMES[recognition.kind]}` : 'Add Shaper shape';
  },
  footprint: (document, params, aliases) => createFootprint(document, params, aliases),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    validateCreate(document, params, context, diagnostics);
    if (kit && !kit.recognizeShaperGesture(params.points.map(point))) diagnostics.push(error('COMMAND_REJECTED', 'The gesture was not recognised as a rectangle, an ellipse or a triangle.'));
    return diagnostics;
  },
  ...lazyKit,
  compile: (document, params, prepared, aliases) => {
    const vector = kitFor(prepared);
    const recognition = vector.recognizeShaperGesture(params.points.map(point));
    if (!recognition) throw new IntentRejection('COMMAND_REJECTED', 'The gesture was not recognised as a rectangle, an ellipse or a triangle.');
    const layer = vector.createShaperLayer(recognition);
    return place(document, layer, params, aliases, layer.transform);
  },
});

// ---------------------------------------------------------------------------
// Scene operations (UpdateVectorSceneCommand), as in the Vector inspector
// ---------------------------------------------------------------------------

type SceneUpdate = (objects: readonly VectorObject[]) => VectorObject[];

interface SceneSpec<P extends LayerParams> {
  id: string;
  backs: readonly string[];
  label: (params: P, document: EditorDocument) => string;
  validate: (document: EditorDocument, params: P, context: ValidateContext) => Diagnostic[];
  /** Built at compile time from the params; the closure stays inside the native command. */
  update: (vector: VectorKit, params: P, layer: VectorLayer, document: EditorDocument) => SceneUpdate;
}

function sceneCommand<P extends LayerParams>(spec: SceneSpec<P>): EditorCommand {
  return defineCommand<P>({
    id: spec.id,
    group: 'vector',
    backs: spec.backs,
    label: spec.label,
    footprint: layerFootprint,
    validate: spec.validate,
    ...lazyKit,
    compile: (document, params, prepared, aliases) => {
      const vector = kitFor(prepared);
      const layer = requireVectorLayer(document, params.layer, aliases);
      return [new vector.UpdateVectorSceneCommand(layer.id, spec.label(params, document), spec.update(vector, params, layer, document))];
    },
  });
}

const selected = (ids: readonly string[]) => (item: VectorObject) => ids.includes(item.id);

export const duplicateObjects = sceneCommand<ObjectsParams>({
  id: 'vector.duplicateObjects',
  backs: ['panel.vector-inspector.duplicate-objects'],
  label: () => 'Duplicate vector objects',
  validate: (document, params, context) => validateObjects(document, params, context).diagnostics,
  update: (vector, params) => (objects) => objects.flatMap((item) => params.objects.includes(item.id) ? [item, vector.cloneVectorObject(item)] : [item]),
});

type ReorderParams = ObjectsParams & { to: 'front' | 'back' | 'forward' | 'backward' };
const REORDER_LABELS = { front: 'Bring objects to front', back: 'Send objects to back', forward: 'Bring objects forward', backward: 'Send objects backward' } as const;

function stepObjects(objects: readonly VectorObject[], ids: readonly string[], direction: 1 | -1): VectorObject[] {
  const next = [...objects];
  const isSelected = selected(ids);
  const order = direction === 1 ? next.map((_, index) => next.length - 2 - index).filter((index) => index >= 0) : next.map((_, index) => index + 1).filter((index) => index < next.length);
  for (const index of order) {
    const current = next[index];
    const neighbour = next[index + direction];
    if (current && neighbour && isSelected(current) && !isSelected(neighbour)) {
      next[index] = neighbour;
      next[index + direction] = current;
    }
  }
  return next;
}

export const reorderObjects = sceneCommand<ReorderParams>({
  id: 'vector.reorderObjects',
  backs: ['panel.vector-inspector.bring-to-front', 'panel.vector-inspector.send-to-back'],
  label: (params) => REORDER_LABELS[params.to],
  validate: (document, params, context) => validateObjects(document, params, context).diagnostics,
  update: (_vector, params) => (objects) => {
    const isSelected = selected(params.objects);
    if (params.to === 'front') return [...objects.filter((item) => !isSelected(item)), ...objects.filter(isSelected)];
    if (params.to === 'back') return [...objects.filter(isSelected), ...objects.filter((item) => !isSelected(item))];
    return stepObjects(objects, params.objects, params.to === 'forward' ? 1 : -1);
  },
});

type BooleanOperation = 'unite' | 'subtract' | 'intersect' | 'exclude';

function combineUpdate(vector: VectorKit, ids: readonly string[], operation: BooleanOperation): SceneUpdate {
  return (objects) => {
    const sources = objects.filter(selected(ids));
    const result = vector.combineVectorObjects(sources, operation);
    if (!result) return [...objects];
    const insertion = Math.max(...sources.map((item) => objects.indexOf(item)));
    return objects.flatMap((item, index) => {
      if (index === insertion) return [result];
      return ids.includes(item.id) ? [] : [item];
    });
  };
}

export const pathfinder = sceneCommand<ObjectsParams & { operation: BooleanOperation }>({
  id: 'vector.pathfinder',
  backs: ['panel.vector-inspector.pathfinder'],
  label: (params) => `Pathfinder ${params.operation}`,
  validate: (document, params, context) => validateObjects(document, params, context, 2).diagnostics,
  update: (vector, params) => combineUpdate(vector, params.objects, params.operation),
});

export const shapeBuilder = sceneCommand<ObjectsParams & { operation: 'merge' | 'delete-overlap' }>({
  id: 'vector.shapeBuilder',
  backs: ['panel.vector-inspector.shape-builder-merge', 'panel.vector-inspector.shape-builder-delete-overlap'],
  label: (params) => params.operation === 'merge' ? 'Shape Builder merge' : 'Shape Builder delete overlap',
  validate: (document, params, context) => validateObjects(document, params, context, 2).diagnostics,
  update: (vector, params) => combineUpdate(vector, params.objects, params.operation === 'merge' ? 'unite' : 'exclude'),
});

type AlignOperation = 'align-left' | 'align-center-x' | 'align-right' | 'align-top' | 'align-center-y' | 'align-bottom' | 'distribute-horizontal' | 'distribute-vertical';
type AlignParams = ObjectsParams & { operation: AlignOperation; reference?: 'selection' | 'artboard' | 'key-object'; keyObject?: ObjectId; spacing?: number | null; roundToPixels?: boolean };

/** The canvas in the layer's local space, as the inspector computes it. */
function artboardBounds(document: EditorDocument, layer: VectorLayer): Rect {
  const layerTransform = layerDocumentTransform(document.root, layer.id);
  const inverse = layerTransform ? invertMatrix(layerTransform) : null;
  const canvas = { x: 0, y: 0, width: document.width, height: document.height };
  return inverse ? transformRectBoundsPrecise(inverse, canvas) : canvas;
}

export const alignObjects = sceneCommand<AlignParams>({
  id: 'vector.alignObjects',
  backs: ['panel.vector-inspector.align', 'panel.vector-inspector.distribute'],
  label: (params) => params.operation.replaceAll('-', ' '),
  validate: (document, params, context) => {
    const distribute = params.operation.startsWith('distribute');
    const spacing = params.spacing ?? null;
    const minimum = distribute && spacing === null ? 3 : 2;
    const result = validateObjects(document, params, context, minimum, 'position');
    if (params.keyObject !== undefined && !params.objects.includes(params.keyObject)) result.diagnostics.push(error('COMMAND_REJECTED', 'keyObject must be one of objects.'));
    return result.diagnostics;
  },
  update: (vector, params, layer, document) => {
    const settings = {
      operation: params.operation,
      reference: params.reference ?? 'selection',
      keyObjectId: params.keyObject ?? params.objects.at(-1) ?? '',
      spacing: params.spacing ?? null,
      roundToPixels: params.roundToPixels ?? true,
      artboardBounds: artboardBounds(document, layer),
    };
    return (objects) => vector.layoutVectorObjects(objects, params.objects, settings);
  },
});

type NodesParams = ObjectsParams & { nodes: string[] };
const AVERAGE_LABELS = { horizontal: 'Average anchors horizontally', vertical: 'Average anchors vertically', both: 'Average anchor positions' } as const;

function validateNodes(document: EditorDocument, params: NodesParams, context: ValidateContext, count: { min?: number; exactly?: number }): Diagnostic[] {
  const { diagnostics, layer, objects } = validateObjects(document, params, context);
  if (layer) checkNodes(objects, params.nodes, diagnostics, layer.id);
  if (count.exactly !== undefined && params.nodes.length !== count.exactly) diagnostics.push(error('COMMAND_REJECTED', `This operation needs exactly ${count.exactly} nodes.`));
  if (count.min !== undefined && params.nodes.length < count.min) diagnostics.push(error('COMMAND_REJECTED', `This operation needs at least ${count.min} nodes.`));
  return diagnostics;
}

export const averageAnchors = sceneCommand<NodesParams & { axis: 'horizontal' | 'vertical' | 'both' }>({
  id: 'vector.averageAnchors',
  backs: ['panel.vector-inspector.average-anchors-x', 'panel.vector-inspector.average-anchors-y', 'panel.vector-inspector.average-anchors-both'],
  label: (params) => AVERAGE_LABELS[params.axis],
  validate: (document, params, context) => validateNodes(document, params, context, { min: 2 }),
  update: (vector, params) => (objects) => vector.averageSelectedNodes(objects, params.objects, params.nodes, params.axis),
});

export const joinEndpoints = sceneCommand<NodesParams>({
  id: 'vector.joinEndpoints',
  backs: ['panel.vector-inspector.join-endpoints'],
  label: () => 'Join path endpoints',
  validate: (document, params, context) => validateNodes(document, params, context, { exactly: 2 }),
  update: (vector, params) => (objects) => vector.joinSelectedEndpoints(objects, params.objects, params.nodes),
});

export const scissors = sceneCommand<NodesParams>({
  id: 'vector.scissors',
  backs: ['panel.vector-inspector.scissors-cut'],
  label: () => 'Scissor path at anchors',
  validate: (document, params, context) => validateNodes(document, params, context, { min: 1 }),
  update: (vector, params) => (objects) => vector.scissorSelectedObjects(objects, params.objects, params.nodes),
});

const COMPOUND_LABELS = { make: 'Make Compound Path', release: 'Release Compound Path', reverse: 'Reverse Path Direction' } as const;

export const compoundPath = sceneCommand<ObjectsParams & { operation: 'make' | 'release' | 'reverse' }>({
  id: 'vector.compoundPath',
  backs: ['panel.vector-inspector.compound-make', 'panel.vector-inspector.compound-release', 'panel.vector-inspector.reverse-path-direction'],
  label: (params) => COMPOUND_LABELS[params.operation],
  validate: (document, params, context) => validateObjects(document, params, context, params.operation === 'make' ? 2 : 1).diagnostics,
  update: (vector, params) => (objects) => params.operation === 'make' ? vector.makeCompoundPath(objects, params.objects)
    : params.operation === 'release' ? vector.releaseCompoundPaths(objects, params.objects)
      : vector.reversePathDirections(objects, params.objects),
});

export const placeOnPerspectivePlane = sceneCommand<ObjectsParams>({
  id: 'vector.placeOnPerspectivePlane',
  backs: ['panel.vector-inspector.place-on-perspective-plane'],
  label: (_params, document) => `Place vectors on ${activePerspectivePlane(document)?.name ?? 'perspective plane'}`,
  validate: (document, params, context) => {
    const { diagnostics } = validateObjects(document, params, context, 1, 'position');
    if (!activePerspectivePlane(document)) diagnostics.push(error('COMMAND_REJECTED', 'The document has no active perspective plane.'));
    return diagnostics;
  },
  update: (vector, params, layer, document) => {
    const plane = activePerspectivePlane(document);
    const layerTransform = layerDocumentTransform(document.root, layer.id);
    if (!plane || !layerTransform) throw new IntentRejection('COMMAND_REJECTED', 'The document has no active perspective plane.', layer.id);
    return (objects) => vector.projectVectorObjectsToPlane(objects, params.objects, layerTransform, plane);
  },
});

type KnifeParams = LayerParams & { from: PointInput; to: PointInput; objects?: ObjectId[] };

export const knife = sceneCommand<KnifeParams>({
  id: 'vector.knife',
  backs: ['tool.knife'],
  label: () => 'Knife cut',
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = vectorLayerFor(document, params.layer, context, diagnostics);
    if (layer && params.objects) checkObjects(layer, params.objects, diagnostics);
    if (params.from.x === params.to.x && params.from.y === params.to.y) diagnostics.push(error('COMMAND_REJECTED', 'The knife line needs two different points.'));
    if (layer && !invertMatrix(layerDocumentTransform(document.root, layer.id) ?? [0, 0, 0, 0, 0, 0])) diagnostics.push(error('COMMAND_REJECTED', 'The layer transform is not invertible.', layer.id));
    return diagnostics;
  },
  update: (vector, params, layer, document) => {
    // The Knife tool: the line is drawn in document space and cut in the layer's space.
    const layerTransform = layerDocumentTransform(document.root, layer.id);
    const inverse = layerTransform ? invertMatrix(layerTransform) : null;
    if (!inverse) throw new IntentRejection('COMMAND_REJECTED', 'The layer transform is not invertible.', layer.id);
    const ids = params.objects ?? layer.vector.objects.map((object) => object.id);
    const start = transformPoint(inverse, point(params.from));
    const end = transformPoint(inverse, point(params.to));
    return (objects) => vector.knifeCutObjects(objects, ids, start, end);
  },
});

// ---------------------------------------------------------------------------
// One object (UpdateVectorObjectCommand, as the inspector's one-shot edits)
// ---------------------------------------------------------------------------

interface ObjectSpec<P extends ObjectParams> {
  id: string;
  backs: readonly string[];
  /** Restricts the object type (paths only for path operations). */
  paths?: boolean;
  /** Which locks refuse the edit for agents. Default 'any' (a fully locked layer). */
  lock?: LockMode;
  validate?(object: VectorObject, params: P, diagnostics: Diagnostic[], layer: VectorLayer): void;
  next(vector: VectorKit, object: VectorObject, params: P): VectorObject;
}

function objectCommand<P extends ObjectParams>(spec: ObjectSpec<P>): EditorCommand {
  return defineCommand<P>({
    id: spec.id,
    group: 'vector',
    backs: spec.backs,
    label: () => 'Edit vector object',
    footprint: layerFootprint,
    validate: (document, params, context) => {
      const diagnostics: Diagnostic[] = [];
      const layer = vectorLayerFor(document, params.layer, context, diagnostics, spec.lock);
      if (!layer) return diagnostics;
      const object = spec.paths ? checkPath(layer, params.object, diagnostics) : checkObjects(layer, [params.object], diagnostics)[0];
      if (object) spec.validate?.(object, params, diagnostics, layer);
      return diagnostics;
    },
    ...lazyKit,
    compile: (document, params, prepared, aliases) => {
      const vector = kitFor(prepared);
      const layer = requireVectorLayer(document, params.layer, aliases);
      const object = spec.paths ? requirePath(layer, params.object) : objectOn(layer, params.object);
      if (!object) throw new IntentRejection('LAYER_NOT_FOUND', `Vector object ${json(params.object)} does not exist on ${layer.id}.`, layer.id);
      const next = spec.next(vector, structuredClone(object), params);
      if (same(next, object)) return [];
      return [new vector.UpdateVectorObjectCommand(layer.id, object.id, () => structuredClone(next))];
    },
  });
}

export const renameObject = objectCommand<ObjectParams & { name: string }>({
  id: 'vector.renameObject',
  backs: ['panel.vector-inspector.object-rename'],
  next: (_vector, object, params) => ({ ...object, name: params.name }),
});

type ClosePathParams = ObjectParams & { closed: boolean; subpath?: number };

export const closePath = objectCommand<ClosePathParams>({
  id: 'vector.closePath',
  backs: ['panel.vector-inspector.path-toggle-closed'],
  paths: true,
  validate: (object, params, diagnostics, layer) => {
    if (object.type === 'path' && (params.subpath ?? 0) >= object.geometry.subpaths.length) diagnostics.push(error('COMMAND_REJECTED', `Path ${object.id} has ${object.geometry.subpaths.length} subpaths.`, layer.id));
  },
  next: (_vector, object, params) => object.type === 'path'
    ? { ...object, geometry: { ...object.geometry, subpaths: object.geometry.subpaths.map((subpath, index) => index === (params.subpath ?? 0) ? { ...subpath, closed: params.closed } : subpath) } }
    : object,
});

type CornerParams = ObjectParams & { radius?: number; nodes?: string[]; mode?: 'relative' | 'absolute' };

export const cornerRadius = objectCommand<CornerParams>({
  id: 'vector.cornerRadius',
  backs: ['panel.vector-inspector.dynamic-corner-radius', 'panel.vector-inspector.dynamic-corner-mode'],
  paths: true,
  validate: (object, params, diagnostics, layer) => {
    if (params.radius === undefined && params.mode === undefined) diagnostics.push(error('COMMAND_REJECTED', 'Give radius, mode or both.', layer.id));
    if (params.nodes) checkNodes([object], params.nodes, diagnostics, layer.id);
  },
  next: (vector, object, params) => {
    if (object.type !== 'path') return object;
    let next: PathObject = object;
    if (params.radius !== undefined) {
      // The inspector's Live Corners field: the radius of the given nodes, or of every node.
      const value = params.radius;
      const targetIds = new Set(params.nodes?.length ? params.nodes : next.geometry.nodes.map((node) => node.id));
      next = {
        ...next,
        geometry: {
          ...next.geometry,
          nodes: next.geometry.nodes.map((node) => {
            if (!targetIds.has(node.id)) return node;
            const base = { ...node };
            delete base.cornerRadius;
            return value > 0 ? { ...base, cornerRadius: Math.max(0, value) } : base;
          }),
        },
      };
    }
    if (params.mode !== undefined) next = vector.withDynamicCornerMode(next, params.mode);
    return next;
  },
});

export const expandCorners = objectCommand<ObjectParams>({
  id: 'vector.expandCorners',
  backs: ['panel.vector-inspector.expand-corners'],
  paths: true,
  next: (vector, object) => object.type === 'path' ? { ...object, geometry: vector.effectiveObjectPathGeometry(object) } : object,
});

export const simplifyPath = objectCommand<ObjectParams & { tolerance?: number }>({
  id: 'vector.simplifyPath',
  backs: ['panel.vector-inspector.simplify-path'],
  paths: true,
  next: (vector, object, params) => object.type === 'path' ? vector.simplifyPathObject(object, Math.max(0.05, params.tolerance ?? 2)) : object,
});

export const outlineStroke = objectCommand<ObjectParams>({
  id: 'vector.outlineStroke',
  backs: ['panel.vector-inspector.outline-stroke'],
  validate: (object, _params, diagnostics, layer) => {
    if (!object.stroke || object.stroke.width <= 0) diagnostics.push(error('COMMAND_REJECTED', `Object ${object.id} has no stroke to outline.`, layer.id));
  },
  next: (vector, object) => vector.outlineStroke(object) ?? object,
});

type SetNodesParams = ObjectParams & { subpaths: SubpathInput[]; fillRule?: 'nonzero' | 'evenodd' };

function nextGeometry(current: PathGeometry, params: SetNodesParams): PathGeometry {
  const nodes: PathNode[] = [];
  const existing = new Map(current.nodes.map((node) => [node.id, node]));
  // An existing node keeps its record (and key order, so unchanged nodes save
  // byte-identical); anchor, handles, continuity and corner radius are replaced.
  const build = (input: NodeInput, subpathId: string): PathNode => {
    const previous = input.id === undefined ? undefined : existing.get(input.id);
    if (!previous) return { id: input.id ?? createId('node'), subpathId, ...nodeShape(input) };
    const next: PathNode = { ...previous, subpathId, ...nodeShape(input) };
    if (input.cornerRadius === undefined) delete next.cornerRadius;
    return next;
  };
  const subpaths = params.subpaths.map((subpath) => {
    const subpathId = subpath.id ?? createId('subpath');
    const created = subpath.nodes.map((node) => build(node, subpathId));
    nodes.push(...created);
    return { id: subpathId, nodeIds: created.map((node) => node.id), closed: subpath.closed ?? false };
  });
  return { ...current, nodes, subpaths, fillRule: params.fillRule ?? current.fillRule };
}

export const setPathNodes = objectCommand<SetNodesParams>({
  id: 'vector.setPathNodes',
  backs: ['tool.move.path-node-drag'],
  paths: true,
  // The Move tool does not drag nodes of a position-locked layer.
  lock: 'position',
  validate: (object, params, diagnostics, layer) => {
    if (!params.subpaths.length || params.subpaths.some((subpath) => !subpath.nodes.length)) diagnostics.push(error('COMMAND_REJECTED', 'A path needs at least one subpath with at least one node.', layer.id));
    const nodeIds = params.subpaths.flatMap((subpath) => subpath.nodes.map((node) => node.id).filter((id): id is string => id !== undefined));
    const subpathIds = params.subpaths.map((subpath) => subpath.id).filter((id): id is string => id !== undefined);
    if (new Set(nodeIds).size !== nodeIds.length || new Set(subpathIds).size !== subpathIds.length) diagnostics.push(error('COMMAND_REJECTED', 'Node and subpath ids must be unique.', layer.id));
    // Ids address existing records; new nodes and subpaths get fresh ids (omit id).
    if (object.type !== 'path') return;
    const knownNodes = new Set(object.geometry.nodes.map((node) => node.id));
    const knownSubpaths = new Set(object.geometry.subpaths.map((subpath) => subpath.id));
    for (const id of nodeIds) if (!knownNodes.has(id)) diagnostics.push(error('LAYER_NOT_FOUND', `Path node ${json(id)} does not exist on ${object.id}; omit id for a new node.`, layer.id));
    for (const id of subpathIds) if (!knownSubpaths.has(id)) diagnostics.push(error('LAYER_NOT_FOUND', `Subpath ${json(id)} does not exist on ${object.id}; omit id for a new subpath.`, layer.id));
  },
  next: (_vector, object, params) => object.type === 'path' ? { ...object, geometry: nextGeometry(object.geometry, params) } : object,
});

// ---------------------------------------------------------------------------
// Deleting nodes and objects, object groups
// ---------------------------------------------------------------------------

export const deleteNodes = defineCommand<ObjectParams & { nodes: string[] }>({
  id: 'vector.deleteNodes',
  group: 'vector',
  backs: ['keyboard.delete'],
  label: () => 'Delete path nodes',
  footprint: layerFootprint,
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = vectorLayerFor(document, params.layer, context, diagnostics);
    const object = layer ? checkPath(layer, params.object, diagnostics) : null;
    if (object && layer) checkNodes([object], params.nodes, diagnostics, layer.id);
    if (!params.nodes.length) diagnostics.push(error('COMMAND_REJECTED', 'Give at least one node.'));
    return diagnostics;
  },
  ...lazyKit,
  compile: (document, params, prepared, aliases) => {
    const vector = kitFor(prepared);
    const layer = requireVectorLayer(document, params.layer, aliases);
    return [new vector.DeletePathNodesCommand(layer.id, params.object, [...params.nodes])];
  },
});

export const deleteObjects = defineCommand<ObjectsParams>({
  id: 'vector.deleteObjects',
  group: 'vector',
  backs: ['keyboard.delete'],
  label: (params) => params.objects.length === 1 ? 'Remove vector object' : 'Remove vector objects',
  footprint: layerFootprint,
  validate: (document, params, context) => validateObjects(document, params, context).diagnostics,
  ...lazyKit,
  compile: (document, params, prepared, aliases) => {
    const vector = kitFor(prepared);
    const layer = requireVectorLayer(document, params.layer, aliases);
    return params.objects.map((id) => new vector.RemoveVectorObjectCommand(layer.id, id));
  },
});

export const groupObjects = defineCommand<ObjectsParams & { name?: string }>({
  id: 'vector.groupObjects',
  group: 'vector',
  backs: ['action.group-objects'],
  label: () => 'Group objects',
  footprint: layerFootprint,
  validate: (document, params, context) => {
    // The native command's own wording, which the UI shows as a toast.
    const { diagnostics } = validateObjects(document, params, context, 1);
    if (params.objects.length < 2) diagnostics.push(error('COMMAND_REJECTED', 'Select at least two objects to group.'));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireVectorLayer(document, params.layer, aliases);
    return [new TagObjectGroupCommand(layer.id, [...params.objects], true, params.name)];
  },
});

export const ungroupObjects = defineCommand<ObjectsParams>({
  id: 'vector.ungroupObjects',
  group: 'vector',
  backs: ['action.ungroup-objects'],
  label: () => 'Ungroup objects',
  footprint: layerFootprint,
  validate: (document, params, context) => {
    const { diagnostics, layer } = validateObjects(document, params, context);
    if (layer && !hasGroupedObject(layer, params.objects)) diagnostics.push(error('COMMAND_REJECTED', 'Nothing selected is grouped.', layer.id));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireVectorLayer(document, params.layer, aliases);
    return [new TagObjectGroupCommand(layer.id, [...params.objects], false)];
  },
});

// ---------------------------------------------------------------------------
// Object transforms (the Move tool's transform frame, as one sealed step)
// ---------------------------------------------------------------------------

/**
 * Drives a gesture command (begin → update → seal) as one discrete command,
 * exactly as CanvasWorkspace does with history.begin/updateLive/commitLive.
 */
class SealedTransformCommand implements Command {
  readonly label: string;

  constructor(private readonly inner: MatrixTransformCommand, private readonly operation: Matrix) {
    this.label = inner.label;
  }

  retainedBytes(): number { return this.inner.retainedBytes() + 64; }

  execute(document: EditorDocument): CommandExecution {
    const started = this.inner.execute(document);
    if (started.outcome !== 'changed') return started;
    this.inner.update(document, this.operation);
    const sealed = this.inner.seal(document);
    if (sealed.outcome !== 'changed') this.inner.undo(document);
    return sealed.outcome === 'no-op' ? noOp() : sealed;
  }

  undo(document: EditorDocument): void { this.inner.undo(document); }
  redo(document: EditorDocument): void { if (this.inner.redo) this.inner.redo(document); else this.inner.execute(document); }
  damage() { return this.inner.damage?.() ?? damage({ fullDocument: true }); }
  dispose(): void { this.inner.dispose?.(); }
}

interface Decomposed { translateX?: number; translateY?: number; rotate?: number; scaleX?: number; scaleY?: number; origin?: PointInput }
type ObjectTransformParams = ObjectsParams & { matrix?: Matrix; decomposed?: Decomposed; scaleStrokes?: boolean };

/** Document-space bounds of objects on a layer. */
function objectsBounds(document: EditorDocument, layer: VectorLayer, ids: readonly string[]): Rect | null {
  const layerTransform = layerDocumentTransform(document.root, layer.id);
  if (!layerTransform) return null;
  const rects = layer.vector.objects.filter(selected(ids)).flatMap((object) => {
    const local = vectorObjectLocalBounds(object);
    return local ? [transformRectBoundsPrecise(multiplyMatrix(layerTransform, object.transform), local)] : [];
  });
  if (!rects.length) return null;
  const left = Math.min(...rects.map((rect) => rect.x));
  const top = Math.min(...rects.map((rect) => rect.y));
  return { x: left, y: top, width: Math.max(...rects.map((rect) => rect.x + rect.width)) - left, height: Math.max(...rects.map((rect) => rect.y + rect.height)) - top };
}

function operationMatrix(document: EditorDocument, layer: VectorLayer, params: ObjectTransformParams): Matrix {
  if (params.matrix) return [...params.matrix] as unknown as Matrix;
  const value = params.decomposed ?? {};
  const bounds = objectsBounds(document, layer, params.objects);
  const origin = value.origin ?? (bounds ? { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 } : { x: 0, y: 0 });
  const radians = (value.rotate ?? 0) * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const scaleX = value.scaleX ?? 1;
  const scaleY = value.scaleY ?? 1;
  const toOrigin: Matrix = [1, 0, 0, 1, -origin.x, -origin.y];
  const linear: Matrix = [cos * scaleX, sin * scaleX, -sin * scaleY, cos * scaleY, 0, 0];
  const back: Matrix = [1, 0, 0, 1, origin.x + (value.translateX ?? 0), origin.y + (value.translateY ?? 0)];
  return multiplyMatrix(back, multiplyMatrix(linear, toOrigin));
}

export const objectTransform = defineCommand<ObjectTransformParams>({
  id: 'vector.objectTransform',
  group: 'vector',
  backs: ['tool.move.translate', 'tool.move.resize-handle', 'tool.move.rotate-handle'],
  label: (params) => params.scaleStrokes === false ? 'Scale vector objects without strokes and effects' : 'Transform vector objects',
  footprint: layerFootprint,
  validate: (document, params, context) => {
    const { diagnostics, layer } = validateObjects(document, params, context, 1, 'position');
    if ((params.matrix === undefined) === (params.decomposed === undefined)) diagnostics.push(error('COMMAND_REJECTED', 'Give exactly one of matrix or decomposed.'));
    if (params.matrix && !invertMatrix(params.matrix)) diagnostics.push(error('COMMAND_REJECTED', 'The matrix is not invertible.'));
    if (params.decomposed && (params.decomposed.scaleX === 0 || params.decomposed.scaleY === 0)) diagnostics.push(error('COMMAND_REJECTED', 'Scale cannot be zero.'));
    if (layer && !layerDocumentTransform(document.root, layer.id)) diagnostics.push(error('LAYER_NOT_FOUND', `Layer ${layer.id} has no document transform.`, layer.id));
    return diagnostics;
  },
  ...lazyKit,
  compile: (document, params, prepared, aliases) => {
    const vector = kitFor(prepared);
    const layer = requireVectorLayer(document, params.layer, aliases);
    const operation = operationMatrix(document, layer, params);
    const inner = params.scaleStrokes === false
      ? new vector.ScaleVectorObjectsWithoutEffectsCommand(layer.id, [...params.objects])
      : new vector.TransformVectorObjectsCommand(layer.id, [...params.objects]);
    return [new SealedTransformCommand(inner, operation)];
  },
});

export const VECTOR_OBJECT_COMMANDS: readonly EditorCommand[] = [
  createPath, createShape, createGrid, shaper,
  duplicateObjects, reorderObjects, pathfinder, shapeBuilder, alignObjects,
  averageAnchors, joinEndpoints, scissors, compoundPath, placeOnPerspectivePlane, knife,
  renameObject, closePath, cornerRadius, expandCorners, simplifyPath, outlineStroke, setPathNodes,
  deleteNodes, deleteObjects, groupObjects, ungroupObjects,
  objectTransform,
];
